import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { Modal, ConfirmModal, Button, FormGroup, FormLabel, FormSelect, FormControl } from '../components/adminModals';
import { Users, BookOpen, ClipboardList, UserX, AlertCircle } from 'lucide-react';
import {
    reviewCategoryService,
    categoryService,
    cycleService,
    eventCycleService,
    reviewerManagementStatsService,
    ReviewerItem,
    Category,
    CreateUpdateReviewerCategoryPayload,
    ReviewerStats,
    Cycle,
    ReviewerManagementStats
} from '../services/adminAuth';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import ReviewerAssessment from './ReviewerAssessment';

// Stat Card Component
interface StatCardProps {
    icon: React.ReactNode;
    iconBg: string;
    title: string;
    value: string | number;
}

function StatCard({ icon, iconBg, title, value }: StatCardProps) {
    return (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <span>{title}</span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{value}</div>
                </div>
                <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
                    {icon}
                </div>
            </div>
        </div>
    );
}

function StatCardSkeleton() {
    return (
        <div className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                    <div className="h-8 bg-gray-200 rounded w-20 mb-1" />
                </div>
                <div className="w-10 h-10 rounded-lg bg-gray-200 flex-shrink-0" />
            </div>
        </div>
    );
}

function ReviewManagement() {
    const { showNotification } = useNotification();
    const [activeTab, setActiveTab] = useState<'directory' | 'stats' | 'assessment'>('directory');

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const [reviewersData, setReviewersData] = useState<ReviewerItem[]>([]);
    const [categoriesData, setCategoriesData] = useState<Category[]>([]);
    const [cyclesData, setCyclesData] = useState<Cycle[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedReviewer, setSelectedReviewer] = useState<ReviewerItem | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [reviewStats, setReviewStats] = useState<ReviewerManagementStats | null>(null);
    const [reviewStatsLoading, setReviewStatsLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [filters, setFilters] = useState({
        search: '',
        isActive: true as boolean | null,
        reviewerCategoryId: 0,
        createdFrom: null as string | null,
        createdTo: null as string | null
    });

    const [statsData, setStatsData] = useState<ReviewerStats[]>([]);
    const [statsLoading, setStatsLoading] = useState(false);
    const [isStatsFilterOpen, setIsStatsFilterOpen] = useState(false);
    const [activeCycleId, setActiveCycleId] = useState<number>(0);

    const [statsCurrentPage, setStatsCurrentPage] = useState(1);
    const [statsPageSize, setStatsPageSize] = useState(10);
    const [statsTotalRecords, setStatsTotalRecords] = useState(0);
    const [statsTotalPages, setStatsTotalPages] = useState(0);

    const [statsFilters, setStatsFilters] = useState({
        search: '',
        categoryId: 0,
        applicationType: 0,
        cycleId: 0
    });

    const [editForm, setEditForm] = useState({
        reviewerId: '',
        reviewerName: '',
        categoryId: 0,
        weight: 0,
        reviewerCategoryId: '00000000-0000-0000-0000-000000000000'
    });

    // ── Bootstrap ──────────────────────────────────────────────────────────────
    useEffect(() => {
        fetchActiveCycle();
        fetchReviewerManagementStats();
        fetchCategories();
        fetchCycles();
    }, []);

    // ── Directory: re-fetch on filter/page changes with debounce for search ───
    useEffect(() => {
        if (activeTab !== 'directory') return;
        const handler = setTimeout(() => {
            fetchReviewers();
        }, 400);
        return () => clearTimeout(handler);
    }, [currentPage, pageSize, filters, activeTab]);

    // ── Stats: re-fetch on filter/page changes with debounce for search ───────
    useEffect(() => {
        if (activeTab !== 'stats') return;
        const handler = setTimeout(() => {
            fetchReviewerStats();
        }, 400);
        return () => clearTimeout(handler);
    }, [statsCurrentPage, statsPageSize, statsFilters, activeTab]);

    // ── Tab switch: trigger immediate fetch ────────────────────────────────────
    useEffect(() => {
        if (activeTab === 'directory') fetchReviewers();
        else if (activeTab === 'stats') fetchReviewerStats();
    }, [activeTab]);

    // ──────────────────────────────────────────────────────────────────────────

    const fetchReviewerManagementStats = async () => {
        setReviewStatsLoading(true);
        try {
            const response = await reviewerManagementStatsService.getReviewerManagementDashboardStatistics();
            if (response.isSuccess) {
                setReviewStats(response.data);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch stats' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while fetching stats' });
        } finally {
            setReviewStatsLoading(false);
        }
    };

    const fetchActiveCycle = async () => {
        try {
            const response = await eventCycleService.getActiveCycleEvent();
            if (response.isSuccess && response.data?.cycle?.id) {
                setActiveCycleId(response.data.cycle.id);
                setStatsFilters(prev => ({ ...prev, cycleId: response.data.cycle.id }));
            } else if (!response.isSuccess && response.message) {
                showNotification('warning', { title: 'Cycle Status', message: response.message });
            }
        } catch (err) {
            }
    };

    const fetchReviewers = async () => {
        setLoading(true);
        try {
            const response = await reviewCategoryService.getAllReviewers({
                pageNumber: currentPage,
                pageSize,
                sortBy: 'Username',
                sortDescending: true,
                search: filters.search,
                isActive: filters.isActive,
                reviewerCategoryId: filters.reviewerCategoryId,
                createdFrom: filters.createdFrom,
                createdTo: filters.createdTo
            });
            if (response.isSuccess) {
                setReviewersData(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch reviewers' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching reviewers' });
            } finally {
            setLoading(false);
        }
    };

    const fetchReviewerStats = async () => {
        setStatsLoading(true);
        try {
            const response = await reviewCategoryService.getReviewerStats({
                pageNumber: statsCurrentPage,
                pageSize: statsPageSize,
                sortBy: '',
                sortDescending: true,
                search: statsFilters.search,
                categoryId: statsFilters.categoryId,
                applicationType: statsFilters.applicationType,
                cycleId: statsFilters.cycleId || activeCycleId
            });
            if (response.isSuccess) {
                setStatsData(response.data.data);
                setStatsTotalRecords(response.data.totalRecords);
                setStatsTotalPages(response.data.totalPages);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch reviewer stats' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching reviewer stats' });
            } finally {
            setStatsLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await categoryService.getAllCategories();
            if (response.isSuccess) setCategoriesData(response.data);
        } catch (err) {
            }
    };

    const fetchCycles = async () => {
        try {
            const response = await cycleService.getAllCycles(true);
            if (response.isSuccess) setCyclesData(response.data);
        } catch (err) {
            }
    };

    // ── Filter handlers ────────────────────────────────────────────────────────

    const handleSearch = (query: string) => {
        setFilters(prev => ({ ...prev, search: query }));
        setCurrentPage(1);
    };

    const handleStatsSearch = (query: string) => {
        setStatsFilters(prev => ({ ...prev, search: query }));
        setStatsCurrentPage(1);
    };

    const handleFilterChange = (field: string, value: string | boolean | number | null) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const handleStatsFilterChange = (field: string, value: string | number) => {
        setStatsFilters(prev => ({ ...prev, [field]: value }));
        setStatsCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilters({ search: '', isActive: true, reviewerCategoryId: 0, createdFrom: null, createdTo: null });
        setCurrentPage(1);
    };

    const handleClearStatsFilters = () => {
        setStatsFilters({ search: '', categoryId: 0, applicationType: 0, cycleId: activeCycleId });
        setStatsCurrentPage(1);
    };

    // ── Pagination handlers ────────────────────────────────────────────────────

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => { setPageSize(size); setCurrentPage(1); };
    const handleStatsPageChange = (page: number) => setStatsCurrentPage(page);
    const handleStatsPageSizeChange = (size: number) => { setStatsPageSize(size); setStatsCurrentPage(1); };

    // ── Edit / Delete handlers ─────────────────────────────────────────────────

    const handleEdit = (reviewer: ReviewerItem) => {
        setSelectedReviewer(reviewer);
        if (reviewer.reviewerCategory) {
            setEditForm({
                reviewerId: reviewer.userId,
                reviewerName: reviewer.person.fullName,
                categoryId: reviewer.reviewerCategory.categoryId,
                weight: reviewer.reviewerCategory.weight,
                reviewerCategoryId: reviewer.reviewerCategory.id
            });
        } else {
            setEditForm({
                reviewerId: reviewer.userId,
                reviewerName: reviewer.person.fullName,
                categoryId: 0,
                weight: 0,
                reviewerCategoryId: '00000000-0000-0000-0000-000000000000'
            });
        }
        setIsEditModalOpen(true);
    };

    const handleDelete = (reviewer: ReviewerItem) => {
        if (!reviewer.reviewerCategory) {
            showNotification('error', { title: 'Error', message: 'This reviewer has no thematic area assigned to delete' });
            return;
        }
        setSelectedReviewer(reviewer);
        setIsDeleteModalOpen(true);
    };

    const handleUpdateReviewer = async () => {
        if (!editForm.categoryId || editForm.weight < 0 || editForm.weight > 5) {
            showNotification('warning', { title: 'Validation Error', message: 'Please select a thematic area and enter a valid weight (0-5)' });
            return;
        }
        setSubmitting(true);
        try {
            const payload: CreateUpdateReviewerCategoryPayload = {
                id: editForm.reviewerCategoryId,
                reviewerId: editForm.reviewerId,
                categoryId: editForm.categoryId,
                weight: editForm.weight
            };
            const response = await reviewCategoryService.createUpdateReviewerCategory(payload);
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Reviewer thematic area updated successfully', timer: 3000 });
                setIsEditModalOpen(false);
                setEditForm({ reviewerId: '', reviewerName: '', categoryId: 0, weight: 0, reviewerCategoryId: '00000000-0000-0000-0000-000000000000' });
                setSelectedReviewer(null);
                await fetchReviewers();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to update reviewer thematic area' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while updating reviewer thematic area' });
            } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedReviewer?.reviewerCategory) return;
        setSubmitting(true);
        try {
            const response = await reviewCategoryService.deleteReviewerCategory(selectedReviewer.reviewerCategory.id);
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Reviewer thematic area deleted successfully', timer: 3000 });
                setIsDeleteModalOpen(false);
                setSelectedReviewer(null);
                await fetchReviewers();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to delete reviewer thematic area' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while deleting reviewer thematic area' });
            } finally {
            setSubmitting(false);
        }
    };

    const getInitials = (firstName: string, lastName: string) =>
        `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

    // ── Filter panels ──────────────────────────────────────────────────────────

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                        value={filters.isActive === null ? '' : filters.isActive.toString()}
                        onChange={(e) => {
                            const value = e.target.value === '' ? null : e.target.value === 'true';
                            handleFilterChange('isActive', value);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All</option>
                        <option value="true">Active</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thematic Area</label>
                    <select
                        value={filters.reviewerCategoryId}
                        onChange={(e) => handleFilterChange('reviewerCategoryId', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value={0}>Select Thematic Area</option>
                        {categoriesData.map((category) => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created From</label>
                    <input
                        type="date"
                        value={filters.createdFrom || ''}
                        onChange={(e) => handleFilterChange('createdFrom', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created To</label>
                    <input
                        type="date"
                        value={filters.createdTo || ''}
                        onChange={(e) => handleFilterChange('createdTo', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>
            <button onClick={handleClearFilters} className="mt-4 text-sm text-blue-600 hover:underline">
                Clear all filters
            </button>
        </div>
    );

    const statsFilterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thematic Area</label>
                    <select
                        value={statsFilters.categoryId}
                        onChange={(e) => handleStatsFilterChange('categoryId', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value={0}>Select Thematic Area</option>
                        {categoriesData.map((category) => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Application Type</label>
                    <select
                        value={statsFilters.applicationType}
                        onChange={(e) => handleStatsFilterChange('applicationType', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value={0}>Select Type</option>
                        <option value={1}>Book</option>
                        <option value={2}>Journal Article</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cycle</label>
                    <select
                        value={statsFilters.cycleId}
                        onChange={(e) => handleStatsFilterChange('cycleId', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value={0}>Select Cycle</option>
                        {cyclesData.map((cycle) => (
                            <option key={cycle.id} value={cycle.id}>
                                {cycle.name}{cycle.id === activeCycleId ? ' (Active)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <button onClick={handleClearStatsFilters} className="mt-4 text-sm text-blue-600 hover:underline">
                Clear all filters
            </button>
        </div>
    );

    // ── Table columns ──────────────────────────────────────────────────────────

    const reviewerColumns: TableColumn<ReviewerItem>[] = [
        {
            key: 'person',
            label: 'Reviewer',
            render: (value, row) => (
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {getInitials(row.person.firstName, row.person.lastName)}
                    </div>
                    <div>
                        <div className="font-medium text-gray-900">{row.person.fullName}</div>
                        <div className="text-sm text-gray-500">{row.person.email}</div>
                        {!row.isVerified && (
                            <div className="flex items-center gap-1 text-red-600 font-medium text-xs">
                                <AlertCircle size={12} />
                                Unverified Account
                            </div>
                        )}
                    </div>
                </div>
            )
        },
        {
            key: 'reviewerCategory',
            label: 'Thematic Area',
            render: (value) => (
                <span className="text-gray-700">
                    {value?.category?.name
                        ? value.category.name
                        : <em className="italic text-gray-500">Unassigned</em>}
                </span>
            )
        },
        {
            key: 'reviewerCategory',
            label: 'Weight',
            render: (value) => <span className="text-gray-900 font-medium">{value?.weight ?? 'N/A'}</span>
        },
        {
            key: 'active',
            label: 'Status',
            render: (value) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${value ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${value ? 'bg-green-500' : 'bg-yellow-500'}`} />
                    {value ? 'Active' : 'Unavailable'}
                </span>
            )
        },
    ];

    const reviewerActions: TableAction<ReviewerItem>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                { label: 'Edit Thematic Area', onClick: handleEdit, className: 'text-blue-600' },
                { label: 'Delete Thematic Area', onClick: handleDelete, className: 'text-red-600' }
            ],
            title: 'Actions'
        }
    ];

    const statsColumns: TableColumn<ReviewerStats>[] = [
        {
            key: 'reviewerName',
            label: 'Reviewer',
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900">{row.reviewerName}</div>
                    <div className="text-sm text-gray-500">{row.reviewerEmail}</div>
                </div>
            )
        },
        {
            key: 'assignedSubmissions',
            label: 'Assigned',
            render: (value) => (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 text-sm font-medium">{value}</span>
            )
        },
        {
            key: 'reviewedSubmissions',
            label: 'Reviewed',
            render: (value) => (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-green-50 text-green-700 text-sm font-medium">{value}</span>
            )
        },
        {
            key: 'completionPercentage',
            label: 'Completion Rate',
            render: (value) => (
                <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                        <div
                            className={`h-2 rounded-full ${value >= 80 ? 'bg-green-500' : value >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${value}%` }}
                        />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{value}%</span>
                </div>
            )
        },
        {
            key: 'reassignedSubmissions',
            label: 'Reassigned',
            render: (value) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium ${value > 0 ? 'bg-orange-50 text-orange-700' : 'bg-gray-50 text-gray-700'}`}>
                    {value}
                </span>
            )
        }
    ];

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            {/* Page Header */}
            <div className="mb-6 flex justify-between items-start">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Review Management</h1>
                    <p className="text-gray-500">Manage reviewer database, assignments, and performance monitoring.</p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {reviewStatsLoading ? (
                    Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
                ) : (
                    <>
                        <StatCard icon={<Users className="w-5 h-5 text-gray-600" />} iconBg="bg-gray-100" title="Total Reviewers" value={reviewStats?.totalReviewers.toLocaleString() ?? '—'} />
                        <StatCard icon={<BookOpen className="w-5 h-5 text-orange-600" />} iconBg="bg-orange-50" title="Without Thematic Area" value={reviewStats?.reviewersWithoutSpecialization.toLocaleString() ?? '—'} />
                        <StatCard icon={<ClipboardList className="w-5 h-5 text-blue-600" />} iconBg="bg-blue-50" title="Active Assignments" value={reviewStats?.activeAssignment.toLocaleString() ?? '—'} />
                        <StatCard icon={<UserX className="w-5 h-5 text-red-600" />} iconBg="bg-red-50" title="Not Assigned" value={reviewStats?.notAssigned.toLocaleString() ?? '—'} />
                    </>
                )}
            </div>

            {/* Tabs */}
            <div className="mb-6">
                <div className="bg-white rounded-xl border border-gray-200 flex">
                    <button
                        onClick={() => setActiveTab('directory')}
                        className={`flex-1 px-6 py-3 font-medium rounded-l-md transition-colors ${activeTab === 'directory' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                        Reviewer Directory
                    </button>
                    <button
                        onClick={() => setActiveTab('assessment')}
                        className={`flex-1 px-6 py-3 font-medium transition-colors ${activeTab === 'assessment' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                        Reviewer Assessment
                    </button>
                    <button
                        onClick={() => setActiveTab('stats')}
                        className={`flex-1 px-6 py-3 font-medium rounded-r-md transition-colors ${activeTab === 'stats' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                        Reviewer Stats
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'directory' && (
                <>
                    <ReusableTable
                        title="Registered Reviewers"
                        columns={reviewerColumns}
                        data={reviewersData}
                        actions={reviewerActions}
                        loading={loading}
                        searchPlaceholder="Search reviewers..."
                        onSearch={handleSearch}
                        emptyMessage="No reviewers found."
                        showSearch={true}
                        showFilter={true}
                        filterPanel={filterPanel}
                        onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                        isFilterOpen={isFilterOpen}
                    />
                    {!loading && totalRecords > 0 && (
                        <div className="px-6 py-4 border-t border-gray-100">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalRecords={totalRecords}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                            />
                        </div>
                    )}
                </>
            )}

            {activeTab === 'assessment' && <ReviewerAssessment />}

            {activeTab === 'stats' && (
                <>
                    <ReusableTable
                        title="Reviewer Performance Statistics"
                        subTitle="Track reviewer productivity and completion rates across submissions."
                        columns={statsColumns}
                        data={statsData}
                        loading={statsLoading}
                        searchPlaceholder="Search reviewers..."
                        onSearch={handleStatsSearch}
                        emptyMessage="No reviewer statistics found."
                        showSearch={true}
                        showFilter={true}
                        filterPanel={statsFilterPanel}
                        onFilterToggle={() => setIsStatsFilterOpen(!isStatsFilterOpen)}
                        isFilterOpen={isStatsFilterOpen}
                    />
                    {!statsLoading && statsTotalRecords > 0 && (
                        <Pagination
                            currentPage={statsCurrentPage}
                            totalPages={statsTotalPages}
                            totalRecords={statsTotalRecords}
                            pageSize={statsPageSize}
                            onPageChange={handleStatsPageChange}
                            onPageSizeChange={handleStatsPageSizeChange}
                        />
                    )}
                </>
            )}

            {/* Edit Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditForm({ reviewerId: '', reviewerName: '', categoryId: 0, weight: 0, reviewerCategoryId: '00000000-0000-0000-0000-000000000000' });
                    setSelectedReviewer(null);
                }}
                title="Edit Reviewer Thematic Area"
                description="Assign or update thematic area and weight for this reviewer"
            >
                <div>
                    <FormGroup>
                        <FormLabel>Reviewer Name</FormLabel>
                        <FormControl type="text" value={editForm.reviewerName} onChange={() => undefined} disabled={true} required />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel required>Thematic Area</FormLabel>
                        <FormSelect
                            value={editForm.categoryId.toString()}
                            onChange={(e) => setEditForm({ ...editForm, categoryId: parseInt(e.target.value) })}
                            disabled={submitting}
                        >
                            <option value="0">Select a thematic area</option>
                            {categoriesData.map((category) => (
                                <option key={category.id} value={category.id}>{category.name}</option>
                            ))}
                        </FormSelect>
                    </FormGroup>

                    <FormGroup>
                        <FormLabel required>Weight (0-5)</FormLabel>
                        <FormSelect
                            value={editForm.weight.toString()}
                            onChange={(e) => setEditForm({ ...editForm, weight: parseInt(e.target.value) })}
                            disabled={submitting}
                        >
                            {[0, 1, 2, 3, 4, 5].map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </FormSelect>
                    </FormGroup>

                    <div className="flex gap-3 mt-6">
                        <Button
                            variant="white"
                            onClick={() => {
                                setIsEditModalOpen(false);
                                setEditForm({ reviewerId: '', reviewerName: '', categoryId: 0, weight: 0, reviewerCategoryId: '00000000-0000-0000-0000-000000000000' });
                                setSelectedReviewer(null);
                            }}
                            className="flex-1"
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button variant="success" onClick={handleUpdateReviewer} className="flex-1" disabled={submitting}>
                            {submitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Updating...
                                </span>
                            ) : 'Update'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Delete Modal */}
            <ConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => { setIsDeleteModalOpen(false); setSelectedReviewer(null); }}
                onConfirm={handleConfirmDelete}
                title="Delete Reviewer Thematic Area"
                message={`Are you sure you want to delete the thematic area assignment for "${selectedReviewer?.person.fullName}"? This will remove their thematic area and weight.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
                isSubmitting={submitting}
            />
        </div>
    );
}

export default ReviewManagement;